import React, { Fragment, useEffect, useState, useMemo } from 'react';
import T from 'prop-types';
// redux
import { setPageOptions } from 'reducers/layout';
import { setBreadcrumbs } from 'reducers/breadcrumbs';
// components
import { FormattedMessage } from 'react-intl';
import Preloader from 'components/Preloader';
import TreatmentProtocolForm from './components/TreatmentProtocolForm';
// endpoints
import { createFarmTreatmentProtocol, createPigGroupTreatmentProtocol } from 'endpoints/treatmentProtocols';
// utils
import { connect } from 'react-redux';
import { toastResponseErrors } from 'utils/responseErrorsHelper';
import { formatTreatmentProtocolPayload } from './helper';
import moment from 'moment';

const CreateReminderTreatmentProtocol = ({
  setBreadcrumbs,
  setPageOptions,
  router: { push },
  location: { state, search },
}) => {
  const reminderOptions = useMemo(() => {
    if (state?.reminderOptions) {
      return state.reminderOptions;
    }

    const urlParams = new URLSearchParams(search);
    const reminderId = urlParams.get('reminderId');

    if (reminderId) {
      const storedOptions = localStorage.getItem(`reminderOptions_${reminderId}`);
      if (storedOptions) {
        try {
          const parsedOptions = JSON.parse(storedOptions);
          return parsedOptions;
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error parsing reminderOptions:', error);
        }
      }
    }

    return null;
  }, [state?.reminderOptions, search]);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  const generateProtocolName = () => {
    const timestamp = moment().format('YYYY-MM-DD_HH-mm-ss');
    return `Treatment_Protocol_${timestamp}`;
  };

  const getInitialValues = () => {
    if (!reminderOptions) return { treatments: [{ product: '', steps: [{}] }] };

    const { comment, treatment_protocol_products_attributes } = reminderOptions;

    return {
      name: generateProtocolName(),
      comment,
      treatments: treatment_protocol_products_attributes.map((product) => ({
        product: {
          id: product.treatment_product_id,
          route: product.route,
          dosage_measure: product.dosage_measure,
          dosage_type: product.dosage_type,
          name: product.name
        },
        repeat: product.treatment_protocol_product_steps_attributes.some(({ repeat }) => repeat),
        steps: product.treatment_protocol_product_steps_attributes.map((step) => {
          const { step_type, step_unit, start_date, end_date, quantity, gallons_value } = step;

          return {
            [step_type]: {
              step_unit,
              start_date: start_date ? moment.utc(start_date).startOf('day') : null,
              end_date: end_date ? moment.utc(end_date).startOf('day') : null,
              quantity,
              gallons_value
            },
            step_type,
            step_unit
          };
        })
      }))
    };
  };

  useEffect(() => {
    const initTimer = setTimeout(() => {
      setIsInitializing(false);
    }, 100);

    return () => clearTimeout(initTimer);
  }, []);

  useEffect(() => {
    return () => {
      // Clean up localStorage on component unmount
      const urlParams = new URLSearchParams(search);
      const reminderId = urlParams.get('reminderId');
      if (reminderId) {
        localStorage.removeItem(`reminderOptions_${reminderId}`);
      }
    };
  }, [search]);

  useEffect(() => {
    if (isInitializing) return;

    if (!reminderOptions) {
      push('/treatment-protocols-farms');
      return;
    }

    setPageOptions({ backLink: '/treatment-protocols-farms' });
    setBreadcrumbs([
      {
        label: <FormattedMessage id="general.treatmentProtocolFarms" />,
        path: '/treatment-protocols-farms',
      },
      {
        label: <FormattedMessage id="general.createTreatmentProtocol" />,
        useLabelAsMobileTitle: true,
      },
    ]);
  }, [reminderOptions, push, isInitializing]);

  useEffect(() => {
    if (reminderOptions) {
      getInitialValues();
    }
  }, [reminderOptions]);

  const onSubmit = async (newProtocolData) => {
    try {
      setIsLoading(true);
      const treatmentProtocolPayload = formatTreatmentProtocolPayload(newProtocolData);

      if (newProtocolData.reminder_type === 'group' && reminderOptions.pig_group) {
        await createPigGroupTreatmentProtocol(
          reminderOptions.pig_group.id,
          treatmentProtocolPayload
        );
        // eslint-disable-next-line max-len
        push(`/treatment-protocols-farms/${reminderOptions.farm.id}/groups/${reminderOptions.pig_group.id}/treatment-protocols`);
      } else {
        await createFarmTreatmentProtocol(
          reminderOptions.farm.id,
          treatmentProtocolPayload
        );
        push(`/treatment-protocols-farms/${reminderOptions.farm.id}/treatment-protocols`);
      }

      // Clean up localStorage after successful creation
      const urlParams = new URLSearchParams(search);
      const reminderId = urlParams.get('reminderId');
      if (reminderId) {
        localStorage.removeItem(`reminderOptions_${reminderId}`);
      }
    } catch (error) {
      toastResponseErrors(error);
      setIsLoading(false);
    }
  };

  if (isInitializing) {
    return (
      <Fragment>
        <Preloader isActive />
      </Fragment>
    );
  }

  if (!reminderOptions) {
    return null;
  }

  return (
    <Fragment>
      <Preloader isActive={isLoading} />
      <TreatmentProtocolForm
        isCreate
        isReminder
        onSubmit={onSubmit}
        initialValues={getInitialValues()}
        title={<FormattedMessage id="general.createTreatmentProtocol" />}
        submitLabel={<FormattedMessage id="general.createTreatmentProtocol" />}
        farm={reminderOptions.farm}
        pigGroup={reminderOptions.pig_group}
      />
    </Fragment>
  );
};

CreateReminderTreatmentProtocol.propTypes = {
  setBreadcrumbs: T.func.isRequired,
  setPageOptions: T.func.isRequired,
  router: T.shape({
    push: T.func.isRequired,
  }).isRequired,
  location: T.shape({
    state: T.shape({
      reminderOptions: T.object,
    }),
    search: T.string,
  }).isRequired,
};

export default connect(null, {
  setPageOptions,
  setBreadcrumbs,
})(CreateReminderTreatmentProtocol);
